import { afterNextRender, Component, inject, Injector, input, Input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDatePickerComponent, NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzI18nService } from 'ng-zorro-antd/i18n';

@Component({
  selector: 'korviu-date-picker',
  templateUrl: './korviu-date-picker.component.html',
  styleUrls: ['./korviu-date-picker.component.scss'],
  imports: [NzFormModule, NzDatePickerComponent, FormsModule, ReactiveFormsModule, NzDatePickerModule],
  standalone: true
})
export class KorviuDatePickerComponent {
  private readonly datePicker = viewChild(NzDatePickerComponent);

  private readonly i18n = inject(NzI18nService);
  private readonly injector = inject(Injector);

  readonly dateFormat = input('');
  readonly defaultPickerValue = input<Date | null>(null);
  readonly dateSelected = output<Date>();

  @Input() control: AbstractControl;
  @Input() label: string;
  @Input() disable: boolean;
  @Input() mode: 'month' | 'date';
  @Input() disabledDate: (currentDate: Date) => boolean;
  @Input() allowClear: boolean = true;

  public isFieldFocusing: boolean;

  private valueWhenOpened: number | null | undefined;

  constructor() {
    // ng-zorro updates its calendar locale, but does not reformat the selected input text.
    this.i18n.localeChange.pipe(takeUntilDestroyed()).subscribe(() => this.datePicker()?.updateInputValue());
  }

  public get formControl(): FormControl {
    return this.control as FormControl;
  }

  public get hasValue(): boolean {
    return typeof this.formControl?.value === 'number' || this.formControl?.value;
  }

  public openCalendar(): void {
    // Wait for the first picker to close and for the calendar's default month to render.
    afterNextRender(
      () => {
        if (!this.formControl.disabled) this.datePicker()?.open();
      },
      { injector: this.injector }
    );
  }

  public onCalendarOpenChange(open: boolean): void {
    const value: unknown = this.formControl.value;
    const timestamp = value instanceof Date && Number.isFinite(value.getTime()) ? value.getTime() : null;

    if (open) {
      this.valueWhenOpened = timestamp;
      return;
    }

    const previous = this.valueWhenOpened;
    this.valueWhenOpened = undefined;
    if (previous !== undefined && timestamp !== null && timestamp !== previous && value instanceof Date) {
      this.dateSelected.emit(value);
    }
  }

  public onDateInputKeyup(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;

    if (target.value === '') {
      if (this.formControl.value !== null) {
        this.formControl.setValue(null, { emitEvent: true });
      }
    }
  }
}
